import type { GameStore } from '../../store/GameStore';
import type { Staff } from '../../core/types';
import { formatCurrency } from '../../i18n/i18n';
import { openStaffDetailDialog } from '../dialogs/StaffDetailDialog';

type SortKey = 'name' | 'level' | 'role' | 'salary' | 'morale';
type FilterKey = 'all' | 'available' | 'training' | 'on_leave' | 'programmer' | 'designer' | 'artist' | 'sound_engineer' | 'qa_tester' | 'producer';

/**
 * Staff list panel — shows all staff with stats, morale, level.
 * Slides in from the right side of the screen.
 * Sort/filter controls at the top.
 */
export class StaffListPanel {
  private element: HTMLDivElement;
  private bodyEl: HTMLDivElement;
  private store: GameStore;
  private visible: boolean = false;
  private sortBy: SortKey = 'name';
  private filterBy: FilterKey = 'all';

  constructor(parent: HTMLElement, store: GameStore) {
    this.store = store;

    this.element = document.createElement('div');
    this.element.className = 'panel';
    this.element.style.cssText = 'position:absolute;top:40px;right:0;bottom:0;width:280px;transform:translateX(100%);transition:transform var(--transition-normal);pointer-events:auto;z-index:10;';

    // Header
    const header = document.createElement('div');
    header.className = 'panel__header';
    const title = document.createElement('span');
    title.textContent = 'Staff';
    header.appendChild(title);
    const closeBtn = document.createElement('button');
    closeBtn.className = 'btn btn--icon';
    closeBtn.textContent = '×';
    closeBtn.addEventListener('click', () => this.hide());
    header.appendChild(closeBtn);
    this.element.appendChild(header);

    // Sort/filter bar
    const controls = document.createElement('div');
    controls.style.cssText = 'display:flex;gap:4px;padding:4px 8px;background:var(--color-bg-primary);border-bottom:1px solid var(--border-color);';

    const sortSelect = document.createElement('select');
    sortSelect.style.cssText = 'flex:1;padding:2px 4px;font-size:10px;background:var(--color-bg-secondary);border:1px solid var(--border-color);border-radius:3px;color:var(--color-text-primary);';
    for (const [val, label] of [['name', 'Name'], ['level', 'Level'], ['role', 'Role'], ['salary', 'Salary'], ['morale', 'Morale']] as const) {
      const opt = document.createElement('option');
      opt.value = val;
      opt.textContent = `Sort: ${label}`;
      sortSelect.appendChild(opt);
    }
    sortSelect.addEventListener('change', () => { this.sortBy = sortSelect.value as SortKey; this.render(); });
    controls.appendChild(sortSelect);

    const filterSelect = document.createElement('select');
    filterSelect.style.cssText = 'flex:1;padding:2px 4px;font-size:10px;background:var(--color-bg-secondary);border:1px solid var(--border-color);border-radius:3px;color:var(--color-text-primary);';
    for (const [val, label] of [['all', 'All'], ['available', 'Available'], ['training', 'Training'], ['on_leave', 'On Leave'], ['programmer', 'Programmers'], ['designer', 'Designers'], ['artist', 'Artists'], ['sound_engineer', 'Sound'], ['qa_tester', 'QA'], ['producer', 'Producers']] as const) {
      const opt = document.createElement('option');
      opt.value = val;
      opt.textContent = label;
      filterSelect.appendChild(opt);
    }
    filterSelect.addEventListener('change', () => { this.filterBy = filterSelect.value as FilterKey; this.render(); });
    controls.appendChild(filterSelect);

    this.element.appendChild(controls);

    // Body
    this.bodyEl = document.createElement('div');
    this.bodyEl.className = 'panel__body';
    this.bodyEl.style.cssText = 'display:flex;flex-direction:column;gap:6px;';
    this.element.appendChild(this.bodyEl);

    parent.appendChild(this.element);
  }

  show(): void {
    this.visible = true;
    this.element.style.transform = 'translateX(0)';
    this.render();
  }

  hide(): void {
    this.visible = false;
    this.element.style.transform = 'translateX(100%)';
  }

  toggle(): void {
    if (this.visible) this.hide();
    else this.show();
  }

  get isVisible(): boolean {
    return this.visible;
  }

  private filterStaff(staff: Staff[]): Staff[] {
    switch (this.filterBy) {
      case 'available': return staff.filter((s) => !s.isTraining && !s.isOnLeave);
      case 'training': return staff.filter((s) => s.isTraining);
      case 'on_leave': return staff.filter((s) => s.isOnLeave);
      case 'programmer': case 'designer': case 'artist': case 'sound_engineer': case 'qa_tester': case 'producer':
        return staff.filter((s) => s.role === this.filterBy);
      default: return staff;
    }
  }

  private sortStaff(staff: Staff[]): Staff[] {
    const sorted = [...staff];
    switch (this.sortBy) {
      case 'name': sorted.sort((a, b) => a.name.localeCompare(b.name)); break;
      case 'level': sorted.sort((a, b) => b.level - a.level); break;
      case 'role': sorted.sort((a, b) => a.role.localeCompare(b.role)); break;
      case 'salary': sorted.sort((a, b) => b.salary - a.salary); break;
      case 'morale': sorted.sort((a, b) => a.morale - b.morale); break;
    }
    return sorted;
  }

  render(): void {
    if (!this.visible) return;
    this.bodyEl.innerHTML = '';

    const state = this.store.getState();
    const theme = this.store.getThemeData();

    let staffList = this.filterStaff([...state.staff]);
    staffList = this.sortStaff(staffList);

    if (staffList.length === 0) {
      const empty = document.createElement('div');
      empty.textContent = state.staff.length === 0 ? 'No staff yet.' : 'No staff match this filter.';
      empty.style.cssText = 'font-size:11px;color:var(--color-text-muted);text-align:center;padding:16px;';
      this.bodyEl.appendChild(empty);
      return;
    }

    const statColors: Record<string, string> = {
      coding: 'var(--color-stat-coding)', art: 'var(--color-stat-art)',
      sound: 'var(--color-stat-sound)', design: 'var(--color-stat-design)',
    };

    for (const staff of staffList) {
      const card = document.createElement('div');
      card.className = 'card';
      card.style.cssText = 'display:flex;flex-direction:column;gap:4px;padding:8px;cursor:pointer;';
      card.addEventListener('click', () => {
        openStaffDetailDialog(this.element.parentElement ?? document.body, this.store, staff.id, () => {
          this.render();
        });
      });

      // Name row
      const nameRow = document.createElement('div');
      nameRow.style.cssText = 'display:flex;justify-content:space-between;align-items:center;';
      const nameEl = document.createElement('strong');
      nameEl.textContent = staff.name;
      nameEl.style.fontSize = '12px';
      const levelEl = document.createElement('span');
      levelEl.textContent = `Lv.${staff.level}`;
      levelEl.style.cssText = 'font-size:11px;color:var(--color-xp);font-weight:600;';
      nameRow.appendChild(nameEl);
      nameRow.appendChild(levelEl);
      card.appendChild(nameRow);

      // Role + salary
      const roleRow = document.createElement('div');
      roleRow.style.cssText = 'display:flex;justify-content:space-between;font-size:10px;color:var(--color-text-secondary);';
      const roleDef = theme.entities.staffRoles.find((r) => r.id === staff.role) ?? theme.entities.founderRole;
      const roleEl = document.createElement('span');
      roleEl.textContent = roleDef.name + (staff.isCEO ? ' (CEO)' : '');
      const salaryEl = document.createElement('span');
      salaryEl.textContent = staff.isCEO ? '' : `${formatCurrency(staff.salary)}/wk`;
      salaryEl.style.fontFamily = 'var(--font-pixel)';
      roleRow.appendChild(roleEl);
      roleRow.appendChild(salaryEl);
      card.appendChild(roleRow);

      // Stats
      const statsRow = document.createElement('div');
      statsRow.style.cssText = 'display:flex;gap:6px;flex-wrap:wrap;';
      for (const [stat, value] of Object.entries(staff.stats)) {
        const el = document.createElement('span');
        el.style.cssText = `font-size:10px;color:${statColors[stat] ?? 'var(--color-text-muted)'};`;
        el.textContent = `${stat.slice(0, 3)}: ${Math.round(value)}`;
        statsRow.appendChild(el);
      }
      card.appendChild(statsRow);

      // Morale + energy bars
      const barsRow = document.createElement('div');
      barsRow.style.cssText = 'display:flex;gap:4px;align-items:center;';
      const moraleColor = staff.morale > 60 ? 'var(--color-morale-high)' : staff.morale > 30 ? 'var(--color-morale-mid)' : 'var(--color-morale-low)';

      const moraleBar = document.createElement('div');
      moraleBar.className = 'progress-bar';
      moraleBar.style.cssText = 'flex:1;height:6px;';
      moraleBar.title = `Morale: ${Math.round(staff.morale)}%`;
      const moraleFill = document.createElement('div');
      moraleFill.className = 'progress-bar__fill';
      moraleFill.style.cssText = `width:${staff.morale}%;background:${moraleColor};`;
      moraleBar.appendChild(moraleFill);

      const energyBar = document.createElement('div');
      energyBar.className = 'progress-bar';
      energyBar.style.cssText = 'flex:1;height:6px;';
      energyBar.title = `Energy: ${Math.round(staff.energy)}%`;
      const energyFill = document.createElement('div');
      energyFill.className = 'progress-bar__fill';
      energyFill.style.cssText = `width:${staff.energy}%;background:var(--color-info);`;
      energyBar.appendChild(energyFill);

      barsRow.appendChild(document.createTextNode('😊'));
      barsRow.appendChild(moraleBar);
      barsRow.appendChild(document.createTextNode('⚡'));
      barsRow.appendChild(energyBar);
      card.appendChild(barsRow);

      // Senior role badge
      if (staff.seniorRole) {
        const srDef = theme.entities.seniorRoles?.find((r) => r.id === staff.seniorRole);
        if (srDef) {
          const tag = document.createElement('span');
          tag.textContent = srDef.name;
          tag.style.cssText = 'font-size:9px;color:var(--color-warning);font-weight:600;';
          card.appendChild(tag);
        }
      }

      // Status tags
      if (staff.isTraining) {
        const tag = document.createElement('span');
        tag.textContent = `Training ${staff.trainingTarget ?? ''}`;
        tag.style.cssText = 'font-size:9px;color:var(--color-warning);';
        card.appendChild(tag);
      } else if (staff.isOnLeave) {
        const tag = document.createElement('span');
        tag.textContent = 'On Leave';
        tag.style.cssText = 'font-size:9px;color:var(--color-info);';
        card.appendChild(tag);
      } else if (staff.isOnNotice) {
        const tag = document.createElement('span');
        tag.textContent = 'Leaving Soon!';
        tag.style.cssText = 'font-size:9px;color:var(--color-danger);font-weight:600;';
        card.appendChild(tag);
      }

      this.bodyEl.appendChild(card);
    }
  }

  destroy(): void {
    this.element.remove();
  }
}
